import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Alert,
  Breadcrumb,
  BreadcrumbItem,
  Bullseye,
  Button,
  Card,
  CardBody,
  Content,
  PageSection,
  Spinner,
} from "@patternfly/react-core";

import {
  FileIcon,
  FolderIcon,
} from "@patternfly/react-icons";

import {
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@patternfly/react-table";

import {
  Link,
  useNavigate,
  useParams,
} from "react-router-dom";
import { AppContext } from "shared/context/AppContext";
import { apiClient } from "shared/api/client";

const encodePath = (path = "") => {
  return path
    .split("/")
    .filter(Boolean)
    .map(encodeURIComponent)
    .join("/");
};


const formatFileSize = (size) => {
  if (size === null || size === undefined) {
    return "—";
  }

  if (size === 0) {
    return "0 B";
  }

  const units = [
    "B",
    "KB",
    "MB",
    "GB",
    "TB",
  ];

  const unitIndex = Math.min(
    Math.floor(Math.log(size) / Math.log(1024)),
    units.length - 1,
  );

  const value = size / Math.pow(1024, unitIndex);

  return `${value.toLocaleString("pl-PL", {
    maximumFractionDigits: unitIndex === 0 ? 0 : 1,
  })} ${units[unitIndex]}`;
};


const formatModifiedAt = (value) => {
  if (!value) {
    return "—";
  }

  return new Intl.DateTimeFormat("pl-PL", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
};


const FilesPage = () => {
  const app = useContext(AppContext);
  const params = useParams();
  const navigate = useNavigate();

  

  const apiUrl = (
    app.state.react_app_api_url || ""
  ).replace(/\/+$/, "");

  const currentPath = decodeURIComponent(
    params["*"] || "",
  ).replace(/^\/+|\/+$/g, "");

  const filesBasePath = "/files";

  const [directory, setDirectory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const breadcrumbs = useMemo(() => {
    const parts = currentPath
      .split("/")
      .filter(Boolean);

    return parts.map((name, index) => ({
      name,
      path: parts
        .slice(0, index + 1)
        .join("/"),
    }));
  }, [currentPath]);

    useEffect(() => {
    let isMounted = true;

    const loadDirectory = async () => {
        setIsLoading(true);
        setError(null);

        const encodedPath = encodePath(currentPath);

        const endpoint = encodedPath
            ? `${apiUrl}/ti/api/files/${encodedPath}/`
            : `${apiUrl}/ti/api/files/`;

        console.log("Files API:", endpoint);

        try {
        const data = await apiClient(endpoint);

        if (isMounted) {
            setDirectory(data);
        }
        } catch (requestError) {
        if (isMounted) {
            setDirectory(null);

            if (requestError.message === "HTTP 401") {
            setError(
                "Zaloguj się, aby przeglądać udostępnione pliki.",
            );
            } else if (requestError.message === "HTTP 403") {
            setError(
                "Nie masz dostępu do udostępnionych plików.",
            );
            } else if (requestError.message === "HTTP 404") {
            setError(
                "Wybrany katalog nie istnieje lub nie jest dostępny.",
            );
            } else {
            setError(
                "Nie udało się pobrać zawartości katalogu.",
            );
            }
        }
        } finally {
        if (isMounted) {
            setIsLoading(false);
        }
        }
    };

    if (apiUrl) {
        loadDirectory();
    } else {
        setIsLoading(false);
        setError("Nie udało się ustalić adresu API.");
    }

    return () => {
        isMounted = false;
    };
    }, [apiUrl, currentPath]);

  const openDirectory = (path) => {
    const encodedPath = encodePath(path);

    navigate(
        encodedPath
        ? `${filesBasePath}/${encodedPath}`
        : filesBasePath,
    );
    };

  return (
    <PageSection>
      <Content component="h1">
        Pliki
      </Content>

      <Content component="p">
        Materiały dostępne dla zalogowanych użytkowników.
      </Content>

      <Breadcrumb
        aria-label="Ścieżka katalogu"
        style={{ marginBlock: "1rem" }}
      >
        <BreadcrumbItem>
            <Content>
            {currentPath ? (
                <Link to={filesBasePath}>
                Public
                </Link>
            ) : (
                "Public"
            )}
            </Content>
        </BreadcrumbItem>

        {breadcrumbs.map((breadcrumb, index) => {
          const isLast =
            index === breadcrumbs.length - 1;

          const url =
            `${filesBasePath}/${encodePath(
                breadcrumb.path,
            )}`;

          return (
            <BreadcrumbItem
              key={breadcrumb.path}
              isActive={isLast}
            >
              <Content>
                {isLast ? (
                  breadcrumb.name
                ) : (
                  <Link to={url}>
                    {breadcrumb.name}
                  </Link>
                )}
              </Content>
            </BreadcrumbItem>
          );
        })}
      </Breadcrumb>

      {isLoading && (
        <Bullseye style={{ minHeight: "240px" }}>
          <Spinner aria-label="Ładowanie plików" />
        </Bullseye>
      )}

      {!isLoading && error && (
        <Alert
          variant="danger"
          title="Nie udało się wyświetlić plików"
        >
          {error}
        </Alert>
      )}

      {!isLoading && !error && directory && (
        <Card>
          <CardBody>
            {directory.parent_path !== null && (
              <Button
                variant="link"
                isInline
                onClick={() => {
                  openDirectory(
                    directory.parent_path,
                  );
                }}
                style={{ marginBottom: "1rem" }}
              >
                Przejdź do katalogu nadrzędnego
              </Button>
            )}

            {directory.items.length === 0 ? (
              <Alert
                variant="info"
                isInline
                title="Ten katalog jest pusty"
              />
            ) : (
              <Table
                aria-label="Udostępnione pliki"
                variant="compact"
              >
                <Thead>
                  <Tr>
                    <Th>Nazwa</Th>
                    <Th>Rozmiar</Th>
                    <Th>Data modyfikacji</Th>
                  </Tr>
                </Thead>

                <Tbody>
                  {directory.items.map((item) => (
                    <Tr key={item.path}>
                      <Td dataLabel="Nazwa">
                        {item.type === "directory" ? (
                          <Button
                            variant="link"
                            isInline
                            icon={<FolderIcon />}
                            onClick={() => {
                              openDirectory(item.path);
                            }}
                          >
                            {item.name}
                          </Button>
                        ) : (
                          <Button
                            component="a"
                            variant="link"
                            isInline
                            icon={<FileIcon />}
                            href={`${apiUrl}${item.download_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            >
                            {item.name}
                        </Button>
                        )}
                      </Td>

                      <Td dataLabel="Rozmiar">
                        {formatFileSize(item.size)}
                      </Td>

                      <Td dataLabel="Data modyfikacji">
                        {formatModifiedAt(
                          item.modified_at,
                        )}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}
          </CardBody>
        </Card>
      )}
    </PageSection>
  );
};

export default FilesPage;